// Sets a parameter on the browser URL using standard URL conventions
export function setUrlParamOnWindow (key: string, value: any): void {
  const urlObj = new URL(window.location.href)

  // Remove all existing instances of the key
  urlObj.searchParams.delete(key)

  if (value === undefined) {
    // For undefined, we just remove the parameter (already done above)
  } else if (value === null) {
    urlObj.searchParams.set(key, '')
  } else if (Array.isArray(value)) {
    // Handle arrays - check if any items are non-string
    if (value.some((item) => typeof item !== 'string')) {
      // Array contains non-string items - serialize as JSON
      // NOTE: Will throw on attempt to serialize bigint, function, etc.
      urlObj.searchParams.set(key, JSON.stringify(value))
    } else {
      // Array of strings - use repeated keys
      value.forEach((item) => {
        urlObj.searchParams.append(key, String(item))
      })
    }
  } else if (value instanceof Date) {
    urlObj.searchParams.set(key, value.toISOString())
  } else if (typeof value === 'bigint') {
    urlObj.searchParams.set(key, value.toString())
  } else if (typeof value === 'boolean') {
    urlObj.searchParams.set(key, value ? '1' : '0')
  } else if (typeof value === 'function') {
    throw new Error(`Cannot serialize function to URL parameter: ${key}`)
  } else if (typeof value === 'symbol') {
    urlObj.searchParams.set(key, value.toString())
  } else if (typeof value === 'object') {
    urlObj.searchParams.set(key, JSON.stringify(value))
  } else {
    // For remaining primitives (string, number), just convert to string
    urlObj.searchParams.set(key, String(value))
  }

  // Update the browser's current URL without reloading the page
  window.history.replaceState({}, '', urlObj.toString())
}

export function getStringUrlParamFromWindow (key: string): string | undefined {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  if (value === null) {
    return undefined
  }

  return value
}

export function getStringArrayUrlParamFromWindow (key: string): string[] | undefined {
  const urlObj = new URL(window.location.href)
  const values = urlObj.searchParams.getAll(key)

  if (values.length === 0) {
    return undefined
  }

  return values
}

export function getBooleanUrlParamFromWindow (key: string): boolean | null | undefined {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  let result
  if (value === '') {
    result = null
  } else if (value === '1') {
    result = true
  } else if (value === '0') {
    result = false
  }

  return result
}

export function getNumberUrlParamFromWindow (key: string): number | null | undefined {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  if (value === null) {
    return undefined
  } else if (value === '') {
    return null
  }

  const parsedNum = Number(value)
  return isNaN(parsedNum) ? undefined : parsedNum
}

export function getDateUrlParamFromWindow (key: string): Date | null | undefined {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  if (value === null) {
    return undefined
  } else if (value === '') {
    return null
  }

  const parsedDate = new Date(value)
  return isNaN(parsedDate.getTime()) ? undefined : parsedDate
}

export function getJsonUrlParamFromWindow<T = any> (key: string): T | null | undefined {
  const urlObj = new URL(window.location.href)
  const value = urlObj.searchParams.get(key)

  if (value === null) {
    return undefined
  } else if (value === '') {
    return null
  }

  try {
    return JSON.parse(value) as T
  } catch {
    return undefined
  }
}
